import { useEffect, useState } from 'react';

const MOBILE_UA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

/**
 * The same heuristic, resolved synchronously.
 *
 * useIsMobile() deliberately starts false and corrects in an effect, which is
 * right for rendering but wrong for a *guard*: a guard that runs on first paint
 * would read `false` on a real phone and act on it. Anything that has to decide
 * "may this device be here at all" — like the desktop lifetime block on
 * /checkout — must use this instead, or it will bounce mobile buyers mid-purchase.
 */
export function isMobileNow(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor;
  return MOBILE_UA.test(ua) || window.innerWidth < 768;
}

/**
 * Is this an actual handheld — user agent only, no viewport width.
 *
 * This is the one question the CHECKOUT ROUTING may ask, and the width term is
 * why. `window.innerWidth < 768` is right for layout (a narrow window should get
 * the narrow tree) but wrong for "which checkout does this person get": a desktop
 * browser at half-screen, or with devtools docked, would read as mobile and be
 * bounced out to Stripe's hosted page — which is precisely the desktop-goes-
 * hosted behaviour we are meant to have ruled out. Desktop is embedded, always.
 *
 * Safe in the other direction too: every shipping mobile browser (Safari iOS,
 * Chrome/Firefox/Samsung on Android) carries iPhone, iPad or Android in its UA,
 * so no real phone falls through to the embedded page.
 */
export function isPhoneDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return MOBILE_UA.test(navigator.userAgent || navigator.vendor);
}

/**
 * Single definition of "is this a phone", matching the UA + width heuristic the
 * OnboardingFunnel already used to skip the desktop-shaped product simulation.
 * Kept as one hook so the funnel and the checkout page can never disagree about
 * which experience a given visitor is in — a user who saw the simplified mobile
 * paywall must not land on the desktop checkout header.
 *
 * Starts false and resolves in an effect, so SSR/first paint gets the desktop
 * tree and mobile corrects on mount.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => {
      const ua = navigator.userAgent || navigator.vendor;
      setIsMobile(MOBILE_UA.test(ua) || window.innerWidth < 768);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isMobile;
}
